import type { DiscoverExploreSearchFormData } from '../pages/private/Discover/types/discoverExploreSearch.types';
import type {
  DiscoverTourCompany,
  DiscoverTourFilters,
  TourDuration,
  TourItem,
  TourSearchPayload,
} from '../types/tour.types';
import { parseDurationDaysValue } from '../pages/private/Discover/types/discoverExploreSearch.types';
import { DISCOVER_DEFAULT_PAGE_SIZE } from './pagination.utils';
import { getCurrencySymbol } from '../constants/currency';
import { coerceDiscoverString } from './discoverPayload.utils';
import { formatDateWithYear } from './dateUtils';

export function buildTourSearchPayload(
  form: DiscoverExploreSearchFormData,
  filters: DiscoverTourFilters = { companyIds: [], companySearch: '', priceRange: { min: '', max: '' } }
): TourSearchPayload {
  const payload: TourSearchPayload = {
    from_location: form.from.trim(),
    to_location: form.to.trim(),
    limit: DISCOVER_DEFAULT_PAGE_SIZE,
  };

  const depart = form.depart.trim().slice(0, 10);
  if (depart) {
    payload.depart = depart;
  }

  const durationDays = parseDurationDaysValue(form.durationDays);
  if (durationDays != null) {
    payload.duration = durationDays;
  }

  const priceFrom = formatPriceFilterForApi(filters.priceRange.min);
  if (priceFrom) {
    payload.price_from = priceFrom;
  }

  const priceTo = formatPriceFilterForApi(filters.priceRange.max);
  if (priceTo) {
    payload.price_to = priceTo;
  }

  if (filters.companyIds.length > 0) {
    payload.companies = [...filters.companyIds];
  }

  return payload;
}

export function tourItemKey(tour: TourItem, index: number): string {
  const external = coerceDiscoverString(tour.external_id);
  if (external) return external;
  const code = coerceDiscoverString(tour.product_code);
  if (code) return code;
  return `tour-${index}-${coerceDiscoverString(tour.name) ?? 'item'}`;
}

export function formatProviderLabel(value?: string): string {
  const raw = coerceDiscoverString(value) ?? '';
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** Provider with optional supplier in parentheses. */
export function formatTourCompanyLine(tour: TourItem): string | null {
  const provider = formatProviderLabel(coerceDiscoverString(tour.provider));
  const supplier = formatProviderLabel(coerceDiscoverString(tour.supplier_name));

  if (provider && supplier && provider.toLowerCase() !== supplier.toLowerCase()) {
    return `${provider} (${supplier})`;
  }

  return provider || supplier || null;
}

export function tourRouteLabel(tour: TourItem): string {
  const from = coerceDiscoverString(tour.from_location);
  const to = coerceDiscoverString(tour.to_location);
  if (from && to) return `${from} - ${to}`;
  if (from) return from;
  if (to) return to;
  return '';
}

export function tourDateLabel(tour: TourItem): string {
  const depart = coerceDiscoverString(tour.depart_date)?.slice(0, 10);
  if (!depart) return '';
  return formatDateWithYear(depart);
}

export function formatTourDurationDisplay(duration: TourDuration | undefined): string {
  if (duration == null) return '';

  if (typeof duration === 'object') {
    const from = coerceDiscoverString(duration.from) ?? '';
    const to = coerceDiscoverString(duration.to) ?? '';
    if (from && to) return `${from} - ${to}`;
    return from || to;
  }

  if (typeof duration === 'number' && Number.isFinite(duration)) {
    return String(duration);
  }

  return coerceDiscoverString(duration) ?? '';
}

export function tourDurationLabel(tour: TourItem): string {
  return formatTourDurationDisplay(tour.duration);
}

export function sanitizePriceFilterInput(raw: string): string {
  let cleaned = raw.replace(/[^\d.]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex !== -1) {
    const intPart = cleaned.slice(0, dotIndex);
    const fracPart = cleaned.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);
    cleaned = fracPart.length > 0 ? `${intPart}.${fracPart}` : `${intPart}.`;
  }
  return cleaned.slice(0, 16);
}

export function formatPriceFilterForApi(raw: string): string | undefined {
  const trimmed = sanitizePriceFilterInput(raw.trim());
  if (!trimmed || trimmed === '.') return undefined;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return String(n);
}

export function parseTourPriceAmount(tour: TourItem): number | null {
  const raw = tour.price;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw >= 0 ? raw : null;
  }
  const str = coerceDiscoverString(raw);
  if (!str) return null;
  const cleaned = str.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Display price like flight cards: `from` + `$1,234` (currency-aware). */
export function formatTourPriceDisplay(tour: TourItem): string | null {
  const amount = parseTourPriceAmount(tour);
  if (amount == null) return null;
  const symbol = getCurrencySymbol(coerceDiscoverString(tour.currency));
  const formatted = Number.isInteger(amount)
    ? amount.toLocaleString('en-US')
    : amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${symbol}${formatted}`;
}

export function extractTourCompanies(tours: TourItem[]): DiscoverTourCompany[] {
  const byId = new Map<string, DiscoverTourCompany>();
  for (const tour of tours) {
    const name = coerceDiscoverString(tour.supplier_name);
    if (!name) continue;
    if (!byId.has(name)) {
      byId.set(name, { id: name, name });
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Keep selected suppliers visible even when filtered results no longer include them. */
export function mergeTourCompanies(
  base: DiscoverTourCompany[],
  selectedCompanyIds: string[]
): DiscoverTourCompany[] {
  const byId = new Map(base.map(company => [company.id, company]));
  for (const id of selectedCompanyIds) {
    const trimmed = id.trim();
    if (!trimmed || byId.has(trimmed)) continue;
    byId.set(trimmed, { id: trimmed, name: trimmed });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}
